import { useSFetch } from '../hooks/sfetch';
import { createSignal, onMount, Show } from 'solid-js';
import { For } from 'solid-js';
import Text from '../components/Text';
import StateDisplay from '../components/StateDisplay';
import { showConfirm } from '../components/Dialog';
import { useNavigate } from '@solidjs/router';
import { useToast } from '../components/MessageBox';
import { useTranslation } from '../hooks/useTranslation';
import { formatDateTime } from '../services/dateFormat';

export interface Command {
    id: string;
    ownerId: string;
    name: string;
    template: string;
    onlyPrefix: boolean;
    createdAt: string;
}

export default function MyCommands() {

    const sfetch = useSFetch();
    const toast = useToast();
    const { t } = useTranslation();
    const [commands, setCommands] = createSignal<Command[]>([]);
    const [loading, setLoading] = createSignal(true);
    const navigate = useNavigate();

    const [newCommand, setNewCommand] = createSignal<Command>({
        id: "",
        ownerId: "",
        template: "",
        name: "",
        onlyPrefix: false,
        createdAt: ""
    });

    onMount(async () => {
        const data = await sfetch('/api/GrantedUsers/GetCommandPrefabs');
        if (data.ok) setCommands(data.value ?? []);
        setLoading(false);
    });

    function submitNewCommand(e: Event) {
        e.preventDefault();

        sfetch('/api/GrantedUsers/CreateCommandPrefab', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                template: newCommand().template,
                onlyPrefix: newCommand().onlyPrefix,
                name: newCommand().name
            })
        }).then(data => {
            if (data.status == 200) {
                toast?.info(t('COMMAND_CREATED'));
                setCommands([...commands(), {
                    id: "",
                    ownerId: "",
                    template: newCommand().template,
                    onlyPrefix: newCommand().onlyPrefix,
                    name: newCommand().name,
                    createdAt: ""
                }]);
                setNewCommand({
                    id: "",
                    ownerId: "",
                    template: "",
                    onlyPrefix: false,
                    name: "",
                    createdAt: ""
                });
            }
            else {
                toast?.error(t('COMMAND_CREATE_FAILED'));
            }
        });
    }

    async function deleteCommand(id: string) {
        if (!await showConfirm(t('COMMAND_DELETE_CONFIRM'), true)) return;
        sfetch('/api/GrantedUsers/DeleteCommandPrefab/' + id, {
            method: 'DELETE'
        }).then(data => {
            if (data.status == 200) {
                toast?.info(t('COMMAND_DELETED'));
                setCommands(commands().filter(c => c.id != id));
            }
            else {
                toast?.error(t('COMMAND_DELETE_FAILED'));
            }
        });
    }

    return (
        <div class='dashboard-page'>
            <h1><Text val='MYCOMMANDS'></Text></h1>
            <form onSubmit={submitNewCommand} class='fcard h' style={{ "align-items": "center" }}>
                <input
                    type="text"
                    class="search-input"
                    placeholder={t('TEMPLATE')}
                    value={newCommand().template}
                    onInput={(e) => setNewCommand({...newCommand(), template: (e.target as HTMLInputElement).value})}
                />
                <input
                    type="text"
                    class="search-input"
                    placeholder={t('NAME')}
                    value={newCommand().name}
                    onInput={(e) => setNewCommand({...newCommand(), name: (e.target as HTMLInputElement).value})}
                />
                <label class="h" style={{ "align-items": "center", gap: "var(--spacing-sm)", "white-space": "nowrap" }}>
                    <input
                        type="checkbox"
                        name="onlyPrefix"
                        checked={newCommand().onlyPrefix}
                        onInput={(e) => setNewCommand({...newCommand(), onlyPrefix: (e.target as HTMLInputElement).checked})}
                    />
                    <Text val='ONLYPREFIX' />
                </label>
                <button aria-label="Add command"><i class="fa-solid fa-plus" aria-hidden="true"></i></button>
            </form>
            <Show when={loading()}>
                <StateDisplay loading message="LOADING" />
            </Show>
            <Show when={!loading() && commands().length === 0}>
                <StateDisplay empty icon="fa-solid fa-terminal" message="COMMANDS_EMPTY" helpText="COMMANDS_EMPTY_HELP" />
            </Show>
            <Show when={commands().length > 0}>
                <table class="sessiontable">
                    <thead>
                        <tr>
                            <th><Text val="NAME"></Text></th>
                            <th><Text val="TEMPLATE"></Text></th>
                            <th><Text val="ONLYPREFIX"></Text></th>
                            <th><Text val="CREATEDAT"></Text></th>
                            <th><i class="fa-solid fa-pen-to-square"></i></th>
                            <th><i class="fa-solid fa-trash"></i></th>
                        </tr>
                    </thead>
                    <tbody>
                        <For each={commands()}>
                            {(command) => (
                                <tr>
                                    <td>{command.name}</td>
                                    <td>{command.template}</td>
                                    <td>{command.onlyPrefix ? "Yes" : "No"}</td>
                                    <td>{formatDateTime(command.createdAt)}</td>
                                    <td><button onClick={() => navigate("/command/" + command.id)}><i class="fa-solid fa-pen-to-square"></i></button></td>
                                    <td><button onClick={() => deleteCommand(command.id)} class='danger'><i class="fa-solid fa-trash"></i></button></td>
                                </tr>
                            )}
                        </For>
                    </tbody>
                </table>
            </Show>
        </div>
    )
}
